/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  createContext,
  FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'
import { addAccessConfig } from 'lib/axiosConfig'
import jwtDecode, { JwtPayload } from 'jwt-decode'
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from '@azure/msal-react'
import { loginRequestConfig } from 'lib/msalConfig'
import { useAuth0 } from '@auth0/auth0-react'
import { getUserRoles, getUserStores, getMsalAuthToken } from 'hooks/useAuth'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { addAccessConfigWithoutHumps } from 'lib/axiosConfigWithoutHumps'

interface AuthContextTypes {
  userStores: string[]
  userRoles: string[]
  handleLogout: () => void
  handleLogin: () => unknown
  getAuthRouteContent: (
    success: JSX.Element,
    failure: JSX.Element
  ) => JSX.Element
}

interface MyJwtPayload extends JwtPayload {
  permissions: string[]
  contexts: string[]
}

const supportedAuthTypes = {
  AZUREAD: 'AzureAD',
  AUTH0: 'Auth0',
}

const authType =
  window.appConfig.AUTH_PROVIDER === supportedAuthTypes.AUTH0 && !window.Cypress
    ? supportedAuthTypes.AUTH0
    : supportedAuthTypes.AZUREAD

const authIsAd = () => authType === supportedAuthTypes.AZUREAD
const authIsAuth0 = () => authType === supportedAuthTypes.AUTH0

const AuthContext = createContext<AuthContextTypes | null>(null)

const getToken = (getAccessTokenSilently: any) => {
  if (authIsAd()) {
    return getMsalAuthToken()
  }
  if (authIsAuth0()) {
    return getAccessTokenSilently({
      audience: window.appConfig.AUTH0_AUDIENCE,
    })
  }
  return ''
}

const AuthProvider: FC = ({ children }) => {
  const { isLoading, isAuthenticated, loginWithRedirect } = useAuth0()
  const { instance } = useMsal()
  const [userRoles, setUserRoles] = useState<string[]>([])
  const [userStores, setUserStores] = useState<string[]>([])
  const { getAccessTokenSilently, logout } = useAuth0()
  const isLoggedIn = useIsAuthenticated()
  const [isLoadingToken, setIsLoadingToken] = useState(true)

  const handleLogout = () => {
    if (authIsAd()) {
      instance.logoutPopup().catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error)
      })
    } else {
      logout({ returnTo: window.location.origin })
    }
  }

  const handleLogin = () => {
    if (authIsAd()) {
      instance.loginRedirect(loginRequestConfig).catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e)
      })
    }
    if (authIsAuth0()) {
      loginWithRedirect().catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e)
      })
    } else {
      // eslint-disable-next-line no-console
      console.error('No authentication provider configured.')
    }
  }

  const getAuthRouteContent = (success: JSX.Element, failure: JSX.Element) => {
    if (authIsAd()) {
      return isLoggedIn ? (
        <AuthenticatedTemplate>{success}</AuthenticatedTemplate>
      ) : (
        <UnauthenticatedTemplate>{failure}</UnauthenticatedTemplate>
      )
    }
    return isAuthenticated ? success : failure
  }

  useLayoutEffect(() => {
    if (isAuthenticated || isLoggedIn) {
      addAccessConfigWithoutHumps(getAccessTokenSilently)
      addAccessConfig(getAccessTokenSilently).then(() =>
        setIsLoadingToken(false)
      )
    }
  }, [getAccessTokenSilently, isAuthenticated, isLoggedIn])

  useEffect(() => {
    if (isAuthenticated || isLoggedIn) {
      const getDecoded = async () => {
        const token = await getAccessTokenSilently({
          audience: window.appConfig.AUTH0_AUDIENCE,
        })
        const decoded = jwtDecode<MyJwtPayload>(token)
        const permissions = decoded.permissions as string[]
        setUserRoles(permissions as string[])
        setUserStores(decoded.contexts)
      }
      if (authIsAd()) {
        const permissions = getUserRoles(instance)
        const stores = getUserStores(instance)
        setUserRoles(permissions)
        setUserStores(stores)
      } else {
        getDecoded()
      }
    }
  }, [isAuthenticated, isLoggedIn])

  if (
    (authIsAuth0() && isLoading) ||
    (isAuthenticated && isLoadingToken) ||
    (isLoggedIn && isLoadingToken)
  ) {
    return <LoadingIndicator />
  }
  return (
    <AuthContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        userStores,
        userRoles,
        handleLogout,
        handleLogin,
        getAuthRouteContent,
      }}>
      {children}
    </AuthContext.Provider>
  )
}

export { authIsAd, getToken }

export const useAuth = () => useContext(AuthContext) as AuthContextTypes

export default AuthProvider
